/**
 * TettoAiGiornalieroTab — il tetto giornaliero delle funzioni AI per azienda (22/10/2026,
 * `shared/strumenti/tettoAiGiornaliero.ts`). Yuma è nel piano gratuito, in Base e in Pro con un tetto di spesa al giorno
 * per azienda; il Business ne è fuori (`ai_senza_limite_giornaliero`). Qui Simone decide il tetto di ogni piano, il
 * cambio dollaro → euro, le eccezioni delle singole aziende, e vede chi ha speso cosa oggi.
 *
 * Il costo è quello VERO del fornitore (token × listino dei modelli), senza ricarico: non lo vede nessuna azienda.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { tokens } from '../../theme';
import { eurDaCentesimi } from './testiStrumenti';
import { listTenants, type TenantConsoleListItem } from '../../services/tenantService';
import { leggiSpesaAiDiOggi, leggiTettoAi, oggiARoma, salvaTettoAi, type SpesaAiDiOggi } from '../../services/tettoAiService';
import {
  PIANI_COL_TETTO,
  centesimiDaMicrousd,
  tettoInCentesimi,
  type ConfigTettoAi,
  type PianoColTetto,
} from '@shared/strumenti/tettoAiGiornaliero';

const NOME_PIANO: Record<PianoColTetto, string> = { free: 'Gratuito', base: 'Base', pro: 'Pro' };

/** «1,50» → 150 centesimi; vuoto = nessun tetto per quel piano; `undefined` = non è un importo. */
export function centesimiDaTesto(testo: string): number | null | undefined {
  const t = testo.trim().replace(',', '.');
  if (!t) return null;
  const n = Number(t);
  if (!Number.isFinite(n) || n < 0 || n > 1000) return undefined;
  return Math.round(n * 100);
}

const testoDaCentesimi = (c: number | undefined): string => (typeof c === 'number' ? (c / 100).toFixed(2).replace('.', ',') : '');

export const TettoAiGiornalieroTab: React.FC = () => {
  const [config, setConfig] = useState<ConfigTettoAi | null>(null);
  const [motivi, setMotivi] = useState<string[]>([]);
  const [spesa, setSpesa] = useState<SpesaAiDiOggi[]>([]);
  const [aziende, setAziende] = useState<TenantConsoleListItem[]>([]);
  const [caricamento, setCaricamento] = useState(true);
  const [salvataggio, setSalvataggio] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const [esito, setEsito] = useState<string | null>(null);
  const [perPiano, setPerPiano] = useState<Record<PianoColTetto, string>>({ free: '', base: '', pro: '' });
  const [cambio, setCambio] = useState('');
  const [nuovaAzienda, setNuovaAzienda] = useState('');
  const [nuovoTetto, setNuovoTetto] = useState('');

  const carica = useCallback(async () => {
    setCaricamento(true);
    setErrore(null);
    try {
      const [letta, oggi, elenco] = await Promise.all([leggiTettoAi(), leggiSpesaAiDiOggi(), listTenants({ max: 500 })]);
      setConfig(letta.config);
      setMotivi(letta.motivi ?? []);
      setSpesa(oggi);
      setAziende(elenco);
      if (letta.config) {
        setPerPiano({
          free: testoDaCentesimi(letta.config.per_piano.free),
          base: testoDaCentesimi(letta.config.per_piano.base),
          pro: testoDaCentesimi(letta.config.per_piano.pro),
        });
        setCambio(String(letta.config.eur_per_usd).replace('.', ','));
      }
    } catch (e) {
      setErrore(e instanceof Error ? e.message : String(e));
    } finally {
      setCaricamento(false);
    }
  }, []);

  useEffect(() => { void carica(); }, [carica]);

  const nomi = useMemo(() => new Map(aziende.map((a) => [a.id, a.nome_azienda])), [aziende]);
  const pianoDi = useMemo(() => new Map(aziende.map((a) => [a.id, String(a.tier ?? 'base')])), [aziende]);

  const salva = async (modifica: Parameters<typeof salvaTettoAi>[0], fatto: string) => {
    setSalvataggio(true);
    setErrore(null);
    setEsito(null);
    try {
      const c = await salvaTettoAi(modifica);
      setConfig(c);
      setMotivi([]);
      setEsito(fatto);
    } catch (e) {
      setErrore(e instanceof Error ? e.message : String(e));
    } finally {
      setSalvataggio(false);
    }
  };

  const salvaPiani = () => {
    const per_piano: Partial<Record<PianoColTetto, number | null>> = {};
    for (const p of PIANI_COL_TETTO) {
      const c = centesimiDaTesto(perPiano[p]);
      if (c === undefined) {
        setErrore(`Il tetto del piano ${NOME_PIANO[p]} non è un importo valido`);
        return;
      }
      per_piano[p] = c;
    }
    const eur = Number(cambio.trim().replace(',', '.'));
    if (!Number.isFinite(eur) || eur <= 0) {
      setErrore('Il cambio non è un numero valido');
      return;
    }
    void salva({ per_piano, eur_per_usd: eur }, 'Tetti dei piani salvati');
  };

  const aggiungiEccezione = () => {
    const c = centesimiDaTesto(nuovoTetto);
    if (!nuovaAzienda || c === undefined || c === null) {
      setErrore("Scegli l'azienda e scrivi il suo tetto in euro");
      return;
    }
    void salva({ eccezione: { tenant_id: nuovaAzienda, centesimi: c } }, 'Eccezione salvata').then(() => {
      setNuovaAzienda('');
      setNuovoTetto('');
    });
  };

  if (caricamento) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>;
  }

  return (
    <Stack spacing={3}>
      <Typography sx={{ fontFamily: tokens.font.body, color: tokens.color.ash, maxWidth: '80ch' }}>
        Yuma è nel piano gratuito, in Base e in Pro con un tetto di spesa al giorno per azienda; il Business non ha tetto.
        Al tetto Yuma risponde che per oggi ha finito e propone il piano superiore. Il costo è quello vero del fornitore,
        senza ricarico, e non lo vede nessuna azienda.
      </Typography>

      {errore && <Alert severity="error" onClose={() => setErrore(null)}>{errore}</Alert>}
      {esito && <Alert severity="success" onClose={() => setEsito(null)}>{esito}</Alert>}
      {!config && (
        <Alert severity="warning">
          Il tetto non è configurato{motivi.length ? ` (${motivi.join('; ')})` : ''}: chi ha il tetto non può usare Yuma.
          Salvando i tetti dei piani qui sotto la configurazione nasce.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
        <Typography sx={{ fontWeight: 700, mb: 2 }}>Tetto al giorno per azienda</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
          {PIANI_COL_TETTO.map((p) => (
            <TextField
              key={p}
              label={`${NOME_PIANO[p]} (€ al giorno)`}
              value={perPiano[p]}
              onChange={(e) => setPerPiano((x) => ({ ...x, [p]: e.target.value }))}
              inputProps={{ inputMode: 'decimal' }}
              size="small"
              helperText="Vuoto = nessun tetto configurato"
            />
          ))}
          <TextField
            label="Cambio (€ per 1 $)"
            value={cambio}
            onChange={(e) => setCambio(e.target.value)}
            inputProps={{ inputMode: 'decimal' }}
            size="small"
          />
        </Stack>
        <Button variant="contained" onClick={salvaPiani} disabled={salvataggio} sx={{ minHeight: 44 }}>Salva i tetti dei piani</Button>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
        <Typography sx={{ fontWeight: 700, mb: 1 }}>Eccezioni per azienda</Typography>
        <Typography variant="body2" sx={{ color: tokens.color.ash, mb: 2 }}>
          Il tetto di una singola azienda vince su quello del suo piano.
        </Typography>
        {config && Object.keys(config.eccezioni).length > 0 ? (
          <Table size="small" sx={{ mb: 2 }}>
            <TableHead><TableRow><TableCell>Azienda</TableCell><TableCell align="right">Tetto al giorno</TableCell><TableCell /></TableRow></TableHead>
            <TableBody>
              {Object.entries(config.eccezioni).map(([id, c]) => (
                <TableRow key={id}>
                  <TableCell>{nomi.get(id) ?? id}</TableCell>
                  <TableCell align="right">{eurDaCentesimi(c)}</TableCell>
                  <TableCell align="right">
                    <Button size="small" disabled={salvataggio} onClick={() => void salva({ eccezione: { tenant_id: id, centesimi: null } }, 'Eccezione tolta')}>
                      Togli
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Typography variant="body2" sx={{ mb: 2 }}>Nessuna eccezione.</Typography>
        )}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <TextField select size="small" label="Azienda" value={nuovaAzienda} onChange={(e) => setNuovaAzienda(e.target.value)} sx={{ minWidth: 260 }}>
            {aziende.map((a) => <MenuItem key={a.id} value={a.id}>{a.nome_azienda}</MenuItem>)}
          </TextField>
          <TextField size="small" label="Tetto al giorno (€)" value={nuovoTetto} onChange={(e) => setNuovoTetto(e.target.value)} inputProps={{ inputMode: 'decimal' }} />
          <Button variant="outlined" onClick={aggiungiEccezione} disabled={salvataggio} sx={{ minHeight: 44 }}>Aggiungi l'eccezione</Button>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
        <Typography sx={{ fontWeight: 700, mb: 1 }}>Spesa di oggi ({oggiARoma()})</Typography>
        {spesa.length === 0 ? (
          <Typography variant="body2">Nessuna azienda col tetto ha ancora usato le funzioni AI oggi.</Typography>
        ) : (
          <Table size="small">
            <TableHead><TableRow><TableCell>Azienda</TableCell><TableCell align="right">Risposte</TableCell><TableCell align="right">Spesa</TableCell><TableCell align="right">Tetto</TableCell></TableRow></TableHead>
            <TableBody>
              {[...spesa].sort((a, b) => b.microusd - a.microusd).map((r) => {
                const tetto = config ? tettoInCentesimi(config, { tenantId: r.tenant_id, piano: pianoDi.get(r.tenant_id) ?? 'base' }) : null;
                const spesi = config ? centesimiDaMicrousd(r.microusd, config.eur_per_usd) : null;
                return (
                  <TableRow key={r.tenant_id}>
                    <TableCell>{nomi.get(r.tenant_id) ?? r.tenant_id}</TableCell>
                    <TableCell align="right">{r.voci}</TableCell>
                    <TableCell align="right">{spesi === null ? 'non calcolabile' : eurDaCentesimi(spesi)}</TableCell>
                    <TableCell align="right">{tetto === null ? 'non configurato' : eurDaCentesimi(tetto)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Stack>
  );
};

export default TettoAiGiornalieroTab;
